import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'
import { compose, lifecycle, withState } from 'recompose'

import { useTheme } from '../../theme'
import { isKey } from '../../utils'
import IsMounted from '../../utils/is-mounted'

import { Touchable } from '../touchable'
import { Flex } from '../flex'

import getStyles from './styles'

type SnackbarVariant = 'primary' | 'positive' | 'negative'

const variants: SnackbarVariant[] = ['primary', 'positive', 'negative']

const enhanceSnackbar = compose(
  IsMounted,
  withState('currentlyVisible', 'setVisibility', false),
  lifecycle<ISnackbarProps, {}, {}>({
    componentDidMount() {
      if (this.props.runIfMounted) {
        this.props.runIfMounted(() => this.props.setVisibility && this.props.setVisibility(true))
      }
    },
  })
)

function MaybeTouchable(props: { children: React.ReactNode; style: Rule; onClick?: () => void }) {
  const { onClick, children, style, ...additional } = props

  return onClick ? (
    <Touchable component="article" onClick={onClick} style={style} {...additional}>
      {children}
    </Touchable>
  ) : (
    <article {...css(style)} {...additional}>
      {children}
    </article>
  )
}

interface ISnackbarProps {
  children?: React.ReactNode
  variant?: SnackbarVariant
  visible?: boolean
  cta?: React.ReactNode
  icon?: React.ReactNode
  onClick?: () => void
  onDismiss?: () => void
  style?: Rule

  // internal props
  currentlyVisible?: boolean
  isMounted?: boolean
  runIfMounted?: (fn: () => void) => void
  setVisibility?: (value: boolean) => void
}

export const Snackbar: React.ComponentType<Pick<
  ISnackbarProps,
  Exclude<keyof ISnackbarProps, 'currentlyVisible' | 'setVisibility' | 'runIfMounted' | 'isMounted'>
>> = enhanceSnackbar(
  ({
    children,
    variant,
    icon,
    currentlyVisible,
    visible,
    style,
    onClick,
    onDismiss,
    cta,
    setVisibility,
    isMounted,
    runIfMounted,
    ...additional
  }: ISnackbarProps) => {
    const styles = getStyles(useTheme())
    return (
      <Flex style={[styles.wrapper.base, currentlyVisible && visible && styles.wrapper.visible]}>
        <MaybeTouchable
          onClick={onClick}
          style={[
            styles.snackbar.base,
            isKey(styles.snackbar.variants, variant) && styles.snackbar.variants[variant],
            onClick && styles.snackbar.hoverable,
            onClick &&
              isKey(styles.snackbar.hoverable.variants, variant) &&
              styles.snackbar.hoverable.variants[variant],
            style,
          ]}
          {...additional}
        >
          {icon && <div {...css([styles.icon.base])}>{icon}</div>}
          <div {...css([styles.content.base])}>{children}</div>
        </MaybeTouchable>
        {onDismiss && <DismissButton onDismiss={onDismiss} variant={variant} />}
      </Flex>
    )
  }
)

Snackbar.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(variants),
  visible: PropTypes.bool,
  cta: PropTypes.node,
  onClick: PropTypes.func,
  style: PropTypes.object,
}

Snackbar.defaultProps = {
  variant: 'primary',
  visible: true,
}

Snackbar.displayName = 'Snackbar'

interface ISnackbarTitleProps {
  children: React.ReactNode
  style?: Rule
}

/**
 * @type {React.StatelessComponent}
 */
export const SnackbarTitle = ({ children, style = {}, ...additional }: ISnackbarTitleProps) => {
  const styles = getStyles(useTheme())
  return (
    <h1 {...css({ ...styles.title.base, ...style })} {...additional}>
      {children}
    </h1>
  )
}

SnackbarTitle.propTypes = {
  children: PropTypes.node.isRequired,
  style: PropTypes.object,
}

const descriptionVariants = ['regular', 'cta']

interface ISnackbarDescriptionProps {
  children: React.ReactNode
  variant?: 'regular' | 'cta'
  style?: Rule
}

/**
 * @type {React.StatelessComponent}
 */
export const SnackbarDescription = ({ children, variant, style, ...additional }: ISnackbarDescriptionProps) => {
  const styles = getStyles(useTheme())
  return (
    <p
      {...css([
        styles.description.base,
        isKey(styles.description.variants, variant) && styles.description.variants[variant],
        style,
      ])}
      {...additional}
    >
      {children}
    </p>
  )
}

SnackbarDescription.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(descriptionVariants),
  style: PropTypes.object,
}

SnackbarDescription.defaultProps = {
  variant: 'regular',
}

SnackbarDescription.VARIANTS = descriptionVariants

interface IDismissButtonProps {
  onDismiss: () => void
  variant?: 'primary' | 'positive' | 'negative'
}

const DismissButton = ({ onDismiss, variant }: IDismissButtonProps) => {
  const styles = getStyles(useTheme())
  const variantKey = `variant-${variant}`
  return (
    <button
      onClick={onDismiss}
      {...css([styles.dismissButton.base, isKey(styles.dismissButton, variantKey) && styles.dismissButton[variantKey]])}
    >
      <svg height="30" viewBox="0 0 30 30" width="30">
        <path
          d="M23,15.1039547 C23,14.5556223 22.5551682,14.1111111 21.9962337,14.1111111 L15.8888889,14.1111111 L15.8888889,8.00376627 C15.8888889,7.44940147 15.4402805,7 14.8960453,7 L15.1039547,7 C14.5556223,7 14.1111111,7.44483185 14.1111111,8.00376627 L14.1111111,14.1111111 L8.00376627,14.1111111 C7.44940147,14.1111111 7,14.5597195 7,15.1039547 L7,14.8960453 C7,15.4443777 7.44483185,15.8888889 8.00376627,15.8888889 L14.1111111,15.8888889 L14.1111111,21.9962337 C14.1111111,22.5505985 14.5597195,23 15.1039547,23 L14.8960453,23 C15.4443777,23 15.8888889,22.5551682 15.8888889,21.9962337 L15.8888889,15.8888889 L21.9962337,15.8888889 C22.5505985,15.8888889 23,15.4402805 23,14.8960453 L23,15.1039547 Z"
          fill="white"
          transform="translate(15.000000, 15.000000) rotate(-315.000000) translate(-15.000000, -15.000000)"
        />
      </svg>
    </button>
  )
}

DismissButton.propTypes = {
  onDismiss: PropTypes.func,
  variant: PropTypes.string,
}
;(Snackbar as any).VARIANTS = variants // SMELL: used for storybooks only
